import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Sparkles } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { robustPost } from '../lib/robustFetch';
import { queueLead } from '../lib/leadQueue';

const FinalCTASection = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [region, setRegion] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await fetch('/api/regions');
        const data = await response.json();
        setRegions(data.regions || []);
      } catch (err) {
        setRegions([
          'North America', 'South America', 'Europe', 'Africa',
          'Asia', 'Oceania', 'Caribbean', 'Middle East',
        ]);
      }
    };
    fetchRegions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      email,
      whatsapp,
      region,
      gender: gender || null,
      age: age ? parseInt(age) : null,
      source: 'final_cta',
    };

    // Fire-and-forget — we ALWAYS redirect. Queue on total failure.
    (async () => {
      try {
        const response = await robustPost('/api/lead', payload);
        if (!response.ok && response.status === 0) {
          queueLead(payload);
        }
      } catch {
        queueLead(payload);
      }
    })();

    robustPost('/api/analytics', {
      event_type: 'form_submission',
      page: '/final-cta',
      metadata: { source: 'final_cta', region },
    }).catch(() => {});

    navigate('/thank-you');
  };

  return (
    <section id="final-cta" className="section-spacing section-dark">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <Sparkles className="w-12 h-12 text-secondary mx-auto mb-6" />
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-normal mb-6 text-white">
            Ready to Join the Journey?
          </h2>
          <p className="text-lg text-white/80 max-w-[55ch] mx-auto leading-relaxed mb-12">
            Be part of a community that celebrates courage, transformation, and the stories that connect us all.
          </p>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-5">
            <div className="text-left">
              <Label htmlFor="final-email" className="text-sm font-medium mb-2 block text-white/90">
                Email Address
              </Label>
              <Input
                id="final-email"
                type="text"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl glass-input"
                data-testid="final-cta-email-input"
              />
            </div>

            <div className="text-left">
              <Label htmlFor="final-whatsapp" className="text-sm font-medium mb-2 block text-white/90">
                WhatsApp Number
              </Label>
              <Input
                id="final-whatsapp"
                type="text"
                placeholder="+1234567890"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                required
                className="h-12 rounded-xl glass-input"
                data-testid="final-cta-whatsapp-input"
              />
            </div>

            <div className="text-left">
              <Label htmlFor="final-region" className="text-sm font-medium mb-2 block text-white/90">
                Your Region (Optional)
              </Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="h-12 rounded-xl glass-input" id="final-region" data-testid="final-cta-region-select">
                  <SelectValue placeholder="Select your region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-left">
              <Label htmlFor="final-gender" className="text-sm font-medium mb-2 block text-white/90">
                Gender (Optional)
              </Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger className="h-12 rounded-xl glass-input" id="final-gender" data-testid="final-cta-gender-select">
                  <SelectValue placeholder="Select your gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non-binary">Non-binary</SelectItem>
                  <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-left">
              <Label htmlFor="final-age" className="text-sm font-medium mb-2 block text-white/90">
                Age (Optional)
              </Label>
              <Input
                id="final-age"
                type="number"
                placeholder="Your age"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                min="13"
                max="120"
                className="h-12 rounded-xl glass-input"
                data-testid="final-cta-age-input"
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              size="lg"
              className="w-full h-14 rounded-full text-base font-semibold btn-premium pulse-glow"
              data-testid="final-cta-submit-button"
            >
              {loading ? 'Joining...' : 'Join the Community'}
            </Button>

            <p className="text-sm text-center text-white/60 pt-2">
              Join 2,400+ dreamers, nomads, and storytellers. Your story matters.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTASection;